import { useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import ProfileCompletionPercentage from "./ProfileCompletionPercentage";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { ONFIDO_TOOLTIP_PRIORITY } from "../../helper/common";
import { resolveFinancialDisplayError } from "../../Utils/bankStatementClassification";
import { ChainLinkIcon, PencilEditIcon } from "../../helper/svgHelper";
import styles from "./TenantHeaderCard.module.css";

const TenantHeaderCard = ({
  percent,
  percentageLoading = false,
  missingDocs = [],
  kycStatus = "Failed",
  kycState = "failed",
  bankConnected = false,
  financial_info_errors = null,
  bankStatementClassification = null,
  rentalStatus = null,
  guarantorCount = 0,
  flatmateCount = 0,
  onRedoKyc,
  onConnectBank,
  onAddGuarantor,
  onAddFlatmate,
  onSetTargetRent,
  onEditLandlordNote,
  onOpenGuarantor = null,
  disabled = false,
  processing = false,
  user = null,
  llShowOnly = false,
  isGuarantor = false,
}) => {
  const { _t } = useTranslator();
  const [expanded, setExpanded] = useState(false);
  const visibleDocs = expanded ? missingDocs : missingDocs.slice(0, 3);
  const normalizedRentalStatus =
    rentalStatus === null || rentalStatus === undefined
      ? null
      : String(rentalStatus);
  const isKycFailed =
    kycState === _t("declined") ||
    kycState === "declined" ||
    kycState === _t("failed") ||
    kycState === "failed" ||
    kycState === "review";
  const isKycOk = !isKycFailed;
  const isRentalElevated = normalizedRentalStatus?.toLowerCase() === "elevated";
  const hasRentalStatus =
    normalizedRentalStatus !== null &&
    normalizedRentalStatus !== undefined &&
    normalizedRentalStatus.trim() !== "";
  const kycActionClass = isKycOk
    ? `${styles.action} ${styles.actionSecondary}`
    : styles.action;
  const weakActionClass = `${styles.action} ${styles.actionSecondary}`;
  const disabledStyle = disabled
    ? { opacity: 0.5, cursor: "not-allowed" }
    : undefined;
  const disabledTooltip = processing
    ? _t(
        "profileProcessingWait",
        "Your profile is still being processed. Please wait until it's ready."
      )
    : disabled
      ? _t(
          "profileOutdatedRenewFirst",
          "Your profile is outdated. Please renew your profile first."
        )
      : undefined;

  const renderTooltip = (id) =>
    disabled ? (
      <ReactTooltip
        id={id}
        place="top"
        className="react-tooltip-styling"
        render={() => <p>{disabledTooltip}</p>}
      />
    ) : null;

  const getConsiderResult = (breakdown, path) => {
    const pathParts = path.split(".");
    const section = breakdown?.[pathParts.shift()];
    const field = pathParts.reduce((value, part) => {
      if (!value) return undefined;
      return (
        value.breakdown?.[part] ??
        (part === "properties" ? value.properties : value.properties?.[part]) ??
        value[part]
      );
    }, section);

    if (!field) return false;
    if (field === "consider" || field.result === "consider") return true;

    return Object.values(field.properties ?? {}).some(
      (value) => value === "consider"
    );
  };

  const getReviewReason = () => {
    const output =
      user?.onfido_user_kyc?.onfido_workflow_res?.resource?.output ??
      user?.onfido_user_kyc?.onfido_workflow_res?.output;
    const status =
      user?.onfido_user_kyc?.onfido_workflow_res?.resource?.status ??
      user?.onfido_user_kyc?.onfido_workflow_res?.status;

    if (status === "review" && output?.document_type === "unidentified") {
      return llShowOnly
        ? _t(
            "onfidoUnidentifiedDocumentReviewLandlordMessage",
            "The tenant uploaded unsupported identification documents, so identity verification could not be completed successfully. Please ask the tenant to upload a valid government-issued ID with a visible photo. Copies or scanned documents are not accepted."
          )
        : _t(
            "onfidoUnidentifiedDocumentReviewTenantMessage",
            "Please upload a photo of a valid identification document that clearly shows your face. Copies or scanned documents are not accepted."
          );
    }

    const breakdown = output?.document_report?.breakdown;

    if (!breakdown) return _t("onfidoFallbackMessage");

    const isSanctions =
      breakdown?.image_integrity?.breakdown?.supported_document?.properties
        ?.issuing_country_subject_to_sanctions;

    if (isSanctions) {
      return _t("onfidoImageIntegritySanctionsMessage");
    }

    for (const { path, message, explanation } of ONFIDO_TOOLTIP_PRIORITY) {
      if (getConsiderResult(breakdown, path)) {
        if (path?.includes("image_quality")) {
          return `${_t(message)}: ${_t(explanation)}`;
        }
        return _t(message);
      }
    }

    const hasAnyConsider = Object.values(breakdown).some(
      (section) =>
        section?.result === "consider" ||
        Object.values(section?.breakdown ?? {}).some(
          (field) =>
            field?.result === "consider" ||
            Object.values(field?.properties ?? {}).some(
              (value) => value === "consider"
            )
        )
    );

    return hasAnyConsider ? _t("onfidoFallbackMessage") : null;
  };

  const kycReviewReason = getReviewReason();

  const isGuarantorFor = isGuarantor || (user?.gurantor_for?.length ?? 0) > 0;
  const guarantorForCount = user?.gurantor_for?.length ?? 0;
  const canOpenGuarantorFor =
    isGuarantorFor &&
    guarantorForCount > 0 &&
    typeof onOpenGuarantor === "function";

  // Bank behaviour error: classification wins over Algoan error when available;
  // unknown/missing classification falls back to the Algoan error.
  const bankDisplayError = resolveFinancialDisplayError({
    classification: bankStatementClassification,
    financialInfoErrors: financial_info_errors,
    userFinancialInfoErrors: user?.financial_info_errors,
  });
  const hasBankError = Boolean(bankDisplayError.key);
  const bankErrorTitle = bankDisplayError.titleKey
    ? _t(bankDisplayError.titleKey, bankDisplayError.titleFallback)
    : null;
  const bankErrorSubtitle = bankDisplayError.subtitleKey
    ? _t(bankDisplayError.subtitleKey, bankDisplayError.subtitleFallback)
    : null;
  const bankErrorText = hasBankError
    ? bankErrorSubtitle || _t(bankDisplayError.key, bankDisplayError.fallback)
    : null;

  return (
    <section className={styles.card} aria-label="tenant overview">
      <div className={styles.top}>
        <div className={styles.left}>
          <ProfileCompletionPercentage
            percent={percent}
            loading={percentageLoading}
          />
          <div className={styles.missing}>
            <div className={styles.missingText}>
              {_t(
                "docsAwayFromComplete",
                `${missingDocs.length} documents away from a complete profile`,
                undefined,
                { count: missingDocs.length }
              )}
            </div>
            <div className={styles.chips}>
              {visibleDocs.map((d, i) => (
                <span key={i} className={styles.chip}>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  {_t(
                    typeof d === "string" ? d : d[0],
                    typeof d === "string" ? d : d[0],
                    "fixed"
                  )}
                </span>
              ))}
              {missingDocs.length > 3 && (
                <button
                  type="button"
                  className={styles.moreLink}
                  onClick={() => setExpanded((v) => !v)}
                >
                  {expanded
                    ? _t("less", "Less")
                    : `+${missingDocs.length - 3} ${_t("more", "more")}`}
                </button>
              )}
            </div>
          </div>
        </div>
        <aside className={styles.note} aria-labelledby="landlord-note-title">
          <div className={styles.noteHeader}>
            <span id="landlord-note-title" className={styles.noteTitle}>
              {_t("aNoteToLandlords", "A note to landlords")}
            </span>
            {disabled ? (
              <div
                style={{ display: "inline-flex", alignItems: "center" }}
                data-tooltip-id="thc-edit-tooltip"
              >
                <button
                  type="button"
                  className={styles.noteEditBtn}
                  onClick={onEditLandlordNote}
                  disabled={disabled}
                  style={disabledStyle}
                  aria-label={_t("edit", "Edit")}
                >
                  <PencilEditIcon color="currentColor" size={16} />
                </button>
                {renderTooltip("thc-edit-tooltip")}
              </div>
            ) : (
              <button
                type="button"
                className={styles.noteEditBtn}
                onClick={onEditLandlordNote}
                aria-label={_t("edit", "Edit")}
              >
                <PencilEditIcon color="currentColor" size={16} />
              </button>
            )}
          </div>
        </aside>
      </div>

      <div className={styles.grid}>
        <div className={styles.box}>
          <div className={styles.boxLabel}>
            {_t("verification", "Verification")}
          </div>
          <div className={styles.boxValue}>
            {isKycFailed ? (
              <span
                data-tooltip-id="thc-kyc-error"
                className={styles.kycErrorTrigger}
              >
                <span className={styles.kycErrorIcon} aria-hidden="true">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </span>
                <span className={styles.danger}>
                  {_t("error", kycState || "Error")}
                </span>
                {kycState === "review" ? <span>{_t(kycState)}</span> : null}
              </span>
            ) : (
              <span>KYC · {_t(kycState)}</span>
            )}{" "}
            {disabled ? (
              <div
                style={{ display: "inline-block", marginLeft: "auto" }}
                data-tooltip-id="thc-redo-tooltip"
              >
                <button
                  type="button"
                  className={`${kycActionClass} ${styles.redoAction}`}
                  onClick={onRedoKyc}
                  disabled={disabled}
                  style={disabledStyle}
                >
                  <img
                    src="/images/redoIcon.svg"
                    alt=""
                    className={styles.redoIcon}
                    aria-hidden="true"
                  />
                  {_t("redo", "Redo")}
                </button>
                {renderTooltip("thc-redo-tooltip")}
              </div>
            ) : (
              <button
                type="button"
                className={`${kycActionClass} ${styles.redoAction}`}
                onClick={onRedoKyc}
              >
                <img
                  src="/images/redoIcon.svg"
                  alt=""
                  className={styles.redoIcon}
                  aria-hidden="true"
                />
                {_t("redo", "Redo")}
              </button>
            )}
            {isKycFailed && kycReviewReason ? (
              <ReactTooltip
                id="thc-kyc-error"
                place="bottom"
                className={styles.kycTooltip}
                render={() => (
                  <div>
                    <p className={styles.kycTooltipText}>{kycReviewReason}</p>
                  </div>
                )}
              />
            ) : null}
          </div>
        </div>
        <div className={styles.box}>
          <div className={styles.boxLabel}>
            {_t("bankBehaviour", "Bank behaviour")}
          </div>
          <div className={styles.boxValue}>
            {hasBankError ? (
              <span
                data-tooltip-id="thc-bank-error"
                className={styles.kycErrorTrigger}
              >
                <span className={styles.kycErrorIcon} aria-hidden="true">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </span>
                <span className={styles.danger}>{_t("error", "Error")}</span>
              </span>
            ) : (
              <span className={!bankConnected ? styles.dimmed : undefined}>
                {bankConnected
                  ? _t("banklinked", "Linked")
                  : _t("banknotLinked", "Not linked")}
              </span>
            )}{" "}
            {hasBankError ? (
              disabled ? (
                <div
                  style={{ display: "inline-block", marginLeft: "auto" }}
                  data-tooltip-id="thc-bank-retry-tooltip"
                >
                  <button
                    type="button"
                    className={`${styles.action} ${styles.redoAction}`}
                    onClick={onConnectBank}
                    disabled={disabled}
                    style={disabledStyle}
                  >
                    <img
                      src="/images/redoIcon.svg"
                      alt=""
                      className={styles.redoIcon}
                      aria-hidden="true"
                    />
                    {_t("redo", "Redo")}
                  </button>
                  {renderTooltip("thc-bank-retry-tooltip")}
                </div>
              ) : (
                <button
                  type="button"
                  className={`${styles.action} ${styles.redoAction}`}
                  onClick={onConnectBank}
                >
                  <img
                    src="/images/redoIcon.svg"
                    alt=""
                    className={styles.redoIcon}
                    aria-hidden="true"
                  />
                  {_t("redo", "Redo")}
                </button>
              )
            ) : (
              !bankConnected &&
              (disabled ? (
                <div
                  style={{ display: "inline-block", marginLeft: "auto" }}
                  data-tooltip-id="thc-connect-tooltip"
                >
                  <button
                    type="button"
                    className={`${styles.action} ${styles.connectAction}`}
                    onClick={onConnectBank}
                    disabled={disabled}
                    style={disabledStyle}
                  >
                    <ChainLinkIcon
                      className={styles.connectIcon}
                      aria-hidden="true"
                    />
                    {_t("connect", "Connect")}
                  </button>
                  {renderTooltip("thc-connect-tooltip")}
                </div>
              ) : (
                <button
                  type="button"
                  className={`${styles.action} ${styles.connectAction}`}
                  onClick={onConnectBank}
                >
                  <ChainLinkIcon
                    className={styles.connectIcon}
                    aria-hidden="true"
                  />
                  {_t("connect", "Connect")}
                </button>
              ))
            )}
            {hasBankError && bankErrorText ? (
              <ReactTooltip
                id="thc-bank-error"
                place="bottom"
                className={styles.kycTooltip}
                render={() => (
                  <div>
                    {bankErrorTitle ? (
                      <p className={styles.kycTooltipTitle}>{bankErrorTitle}</p>
                    ) : null}
                    <p className={styles.kycTooltipText}>{bankErrorText}</p>
                  </div>
                )}
              />
            ) : null}
          </div>
        </div>
        <div className={styles.box}>
          <div className={styles.boxLabel}>
            {_t("rentalBarometer", "Rental barometer")}
          </div>
          <div
            className={`${styles.boxValue} ${isRentalElevated ? styles.danger : ""}`}
          >
            {hasRentalStatus ? (
              _t(normalizedRentalStatus.toLowerCase(), normalizedRentalStatus)
            ) : (
              <>
                <span className={styles.danger}>{_t("notSet", "Not set")}</span>
                {disabled ? (
                  <div
                    style={{ display: "inline-block", marginLeft: "auto" }}
                    data-tooltip-id="thc-target-rent-tooltip"
                  >
                    <button
                      type="button"
                      className={styles.action}
                      onClick={onSetTargetRent}
                      disabled={disabled}
                      style={disabledStyle}
                    >
                      {_t("set", "Set")}
                    </button>
                    {renderTooltip("thc-target-rent-tooltip")}
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.action}
                    onClick={onSetTargetRent}
                  >
                    {_t("set", "Set")}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        {isGuarantorFor ? (
          <div
            className={styles.box}
            onClick={canOpenGuarantorFor ? onOpenGuarantor : undefined}
            style={canOpenGuarantorFor ? { cursor: "pointer" } : undefined}
            role={canOpenGuarantorFor ? "button" : undefined}
            tabIndex={canOpenGuarantorFor ? 0 : undefined}
            onKeyDown={
              canOpenGuarantorFor
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpenGuarantor();
                    }
                  }
                : undefined
            }
            aria-label={_t("guarantorFor", "Guarantor for")}
          >
            <div className={styles.boxLabel}>
              {_t("guarantorFor", "Guarantor for")}
            </div>
            <div className={styles.boxValue}>
              {guarantorForCount}{" "}
              {canOpenGuarantorFor && (
                <button
                  type="button"
                  className={weakActionClass}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenGuarantor();
                  }}
                >
                  {_t("viewDetails", "View details")}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.box}>
            <div className={styles.boxLabel}>
              {_t("guarantor", "Guarantor")}
            </div>
            <div className={styles.boxValue}>
              {guarantorCount
                ? `${guarantorCount} ${_t("added", "added")}`
                : _t("none", "None")}{" "}
              {disabled ? (
                <div
                  style={{ display: "inline-block", marginLeft: "auto" }}
                  data-tooltip-id="thc-guarantor-tooltip"
                >
                  <button
                    type="button"
                    className={weakActionClass}
                    onClick={onAddGuarantor}
                    disabled={disabled}
                    style={disabledStyle}
                  >
                    + {_t("add", "Add")}
                  </button>
                  {renderTooltip("thc-guarantor-tooltip")}
                </div>
              ) : (
                <button
                  type="button"
                  className={weakActionClass}
                  onClick={onAddGuarantor}
                >
                  + {_t("add", "Add")}
                </button>
              )}
            </div>
          </div>
        )}
        {!isGuarantorFor && (
          <div className={styles.box}>
            <div className={styles.boxLabel}>
              {_t("flatmates", "Flatmates")}
            </div>
            <div className={styles.boxValue}>
              {flatmateCount || 0}{" "}
              {disabled ? (
                <div
                  style={{ display: "inline-block", marginLeft: "auto" }}
                  data-tooltip-id="thc-flatmate-tooltip"
                >
                  <button
                    type="button"
                    className={weakActionClass}
                    onClick={onAddFlatmate}
                    disabled={disabled}
                    style={disabledStyle}
                  >
                    + {_t("add", "Add")}
                  </button>
                  {renderTooltip("thc-flatmate-tooltip")}
                </div>
              ) : (
                <button
                  type="button"
                  className={weakActionClass}
                  onClick={onAddFlatmate}
                >
                  + {_t("add", "Add")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TenantHeaderCard;
